import type { HistoryRow } from "~/helpers/aiChat/chatHistory";
import type { UseFernieChatReturn } from "~/hooks/useFernieChat";

import { Box, ButtonBase, Icon, IconButton, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import fernieButtonSx from "~/components/fernie/fernieButtonSx";
import { CaptionText } from "~/components/Typographies";
import { dayFormat, formatDateLabel } from "~/helpers/timestampFormat";

interface FernieHistoryListProps {
	chat: Pick<
		UseFernieChatReturn,
		| "chatId"
		| "confirmingDeleteId"
		| "handleDeleteChat"
		| "handleLoadChat"
		| "historyNotice"
		| "historyRows"
		| "setConfirmingDeleteId"
	>;
	touch?: boolean;
}

const FernieHistoryList = ({ chat, touch = false }: FernieHistoryListProps) => {
	const { t } = useTranslation("common");
	const targetSx = touch ? { minHeight: 48, minWidth: 48 } : {};
	const actionIconSize = touch ? 20 : 16;

	const isOpenChat = (row: HistoryRow) => row.cachedChat?.id === chat.chatId;

	return (
		<Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflowX: "hidden", overflowY: "auto", px: 2, py: 2 }}>
			{chat.historyNotice && (
				<CaptionText
					sx={{
						backgroundColor: "action.hover",
						borderRadius: "10px",
						color: "text.secondary",
						display: "block",
						fontSize: "0.75rem",
						lineHeight: 1.6,
						px: 1.5,
						py: 1.25
					}}
				>
					{chat.historyNotice}
				</CaptionText>
			)}
			{chat.historyRows.length === 0 ? (
				<Box sx={{ px: 2, py: 8, textAlign: "center" }}>
					<Box
						sx={{
							alignItems: "center",
							backgroundColor: "action.hover",
							borderRadius: "16px",
							display: "inline-flex",
							height: 56,
							justifyContent: "center",
							mb: 2,
							width: 56
						}}
					>
						<Icon sx={{ color: "text.disabled", fontSize: 28 }}>forum</Icon>
					</Box>
					<Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
						{t("fernieChat.panel.noConversations")}
					</Typography>
					<Typography sx={{ color: "text.disabled", fontSize: "0.75rem", mt: 0.5 }}>
						{t("fernieChat.panel.noConversationsHint")}
					</Typography>
				</Box>
			) : (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					{chat.historyRows.map((row) => (
						<Box
							key={row.id}
							sx={{
								"&:hover": { backgroundColor: "action.hover" },
								alignItems: "center",
								backgroundColor: isOpenChat(row) ? "action.selected" : "transparent",
								borderRadius: "10px",
								display: "flex",
								gap: 1.5,
								minWidth: 0,
								pr: 1.5,
								transition: "background-color 0.15s"
							}}
						>
							<ButtonBase
								onClick={() => chat.handleLoadChat(row)}
								sx={{
									...fernieButtonSx.focusRing,
									borderRadius: "10px",
									flex: 1,
									gap: 1.5,
									justifyContent: "flex-start",
									minWidth: 0,
									pl: 1.5,
									py: 1.25,
									textAlign: "left"
								}}
							>
								<Box
									component="span"
									sx={{
										alignItems: "center",
										backgroundColor: isOpenChat(row) ? "primary.main" : "action.hover",
										borderRadius: "8px",
										display: "flex",
										flexShrink: 0,
										height: 32,
										justifyContent: "center",
										width: 32
									}}
								>
									<Icon sx={{ color: isOpenChat(row) ? "primary.contrastText" : "text.secondary", fontSize: 16 }}>
										chat_bubble_outline
									</Icon>
								</Box>
								<Box component="span" sx={{ display: "block", flex: 1, minWidth: 0 }}>
									<Typography
										component="span"
										noWrap
										sx={{
											display: "block",
											fontSize: "0.82rem",
											fontWeight: isOpenChat(row) ? 600 : 500,
											lineHeight: 1.3
										}}
									>
										{row.title}
									</Typography>
									<Typography
										component="span"
										sx={{ color: "text.disabled", display: "block", fontSize: "0.7rem", mt: 0.25 }}
									>
										{formatDateLabel(new Date(row.lastActivity), dayFormat)}
									</Typography>
								</Box>
							</ButtonBase>
							{chat.confirmingDeleteId === row.id ? (
								<Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: touch ? 1.5 : 0.25 }}>
									<Tooltip title={t("fernieChat.panel.confirmDelete")}>
										<IconButton
											aria-label={t("fernieChat.panel.confirmDeleteAria")}
											onClick={() => chat.handleDeleteChat(row)}
											size="small"
											sx={{ color: "error.main", ...targetSx }}
										>
											<Icon sx={{ fontSize: actionIconSize }}>check</Icon>
										</IconButton>
									</Tooltip>
									<Tooltip title={t("fernieChat.panel.keepConversation")}>
										<IconButton
											aria-label={t("fernieChat.panel.cancelDeleteAria")}
											onClick={() => chat.setConfirmingDeleteId(null)}
											size="small"
											sx={{ color: "text.disabled", ...targetSx }}
										>
											<Icon sx={{ fontSize: actionIconSize }}>close</Icon>
										</IconButton>
									</Tooltip>
								</Box>
							) : (
								<IconButton
									aria-label={t("fernieChat.panel.deleteAria")}
									onClick={() => chat.setConfirmingDeleteId(row.id)}
									size="small"
									sx={{
										"&:hover": { backgroundColor: "error.light", color: "error.main" },
										color: "text.disabled",
										...targetSx
									}}
								>
									<Icon sx={{ fontSize: actionIconSize }}>delete_outline</Icon>
								</IconButton>
							)}
						</Box>
					))}
				</Box>
			)}
		</Box>
	);
};

export default FernieHistoryList;
